import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calculator, TrendingUp, Calendar, Zap, Users, Euro, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ReactivationCalculator = ({ onStartDemo }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [contacts, setContacts] = useState('');
  const [ticket, setTicket] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateROI = async () => {
    if (!contacts || !ticket) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/reactivation/calculator`, {
        total_contacts: parseInt(contacts),
        estimated_ticket: parseFloat(ticket),
        industry: 'general'
      });
      
      setResults(response.data);
    } catch (error) {
      console.error('Error calculating ROI:', error);
      // Fallback calculation
      const numContacts = parseInt(contacts);
      const avgTicket = parseFloat(ticket);
      
      setResults({
        reactivation_immediate: {
          total_contacts: numContacts,
          estimated_responses: Math.round(numContacts * 0.25),
          estimated_conversions: Math.round(numContacts * 0.25 * 0.4),
          estimated_revenue: Math.round(numContacts * 0.25 * 0.4 * avgTicket),
          timeframe: '1-7 días'
        },
        birthday_club_annual: {
          total_contacts: numContacts,
          monthly_birthdays: Math.round(numContacts / 12),
          estimated_conversions_annual: Math.round((numContacts / 12) * 0.9 * 0.35 * 12),
          estimated_revenue_annual: Math.round((numContacts / 12) * 0.9 * 0.35 * 12 * avgTicket),
          timeframe: '12 meses'
        },
        comparison: {
          reactivation_first_week: Math.round(numContacts * 0.25 * 0.4 * avgTicket),
          birthday_first_month: Math.round((numContacts / 12) * 0.9 * 0.35 * avgTicket),
          combined_first_month: Math.round(numContacts * 0.25 * 0.4 * avgTicket + (numContacts / 12) * 0.9 * 0.35 * avgTicket)
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
          <Calculator className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold">
            {isSpanish ? 'Calculadora de Ventas' : 'Sales Calculator'}
          </h3>
          <p className="text-slate-400 text-sm">
            {isSpanish ? 'Estima tus ingresos potenciales' : 'Estimate your potential revenue'}
          </p>
        </div>
      </div>

      {/* Input Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
            <Users className="w-4 h-4" />
            {isSpanish ? 'Contactos en tu base de datos' : 'Contacts in your database'}
          </label>
          <Input
            type="number"
            value={contacts}
            onChange={(e) => setContacts(e.target.value)}
            placeholder="500"
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="calc-contacts"
          />
        </div>
        <div>
          <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
            <Euro className="w-4 h-4" />
            {isSpanish ? 'Ticket medio (€)' : 'Average ticket (€)'}
          </label>
          <Input
            type="number"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
            placeholder="45"
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="calc-ticket"
          />
        </div>
      </div>

      <Button
        onClick={calculateROI}
        disabled={!contacts || !ticket || loading}
        className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold mb-6"
        data-testid="calc-button"
      >
        {loading ? (
          isSpanish ? 'Calculando...' : 'Calculating...'
        ) : (
          <>
            <TrendingUp className="w-5 h-5 mr-2" />
            {isSpanish ? 'Calcular Potencial' : 'Calculate Potential'}
          </>
        )}
      </Button>

      {/* Results */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Reactivation Results */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl p-5 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-emerald-400" />
                <h4 className="font-semibold text-emerald-400">
                  {isSpanish ? 'REACTIVACIÓN INMEDIATA' : 'IMMEDIATE REACTIVATION'}
                </h4>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">
                    {results.reactivation_immediate.estimated_responses}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Respuestas' : 'Responses'}
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {results.reactivation_immediate.estimated_conversions}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Ventas' : 'Sales'}
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-400">
                    €{results.reactivation_immediate.estimated_revenue?.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Esta semana' : 'This week'}
                  </p>
                </div>
              </div>
            </div>

            {/* Birthday Club Results */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-5 border border-purple-500/30">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-purple-400" />
                <h4 className="font-semibold text-purple-400">
                  {isSpanish ? 'CLUB VIP CUMPLEAÑOS (Anual)' : 'VIP BIRTHDAY CLUB (Annual)'}
                </h4>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">
                    ~{results.birthday_club_annual.monthly_birthdays}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Cumpleaños/mes' : 'Birthdays/month'}
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {results.birthday_club_annual.estimated_conversions_annual}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Ventas/año' : 'Sales/year'}
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-400">
                    €{results.birthday_club_annual.estimated_revenue_annual?.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isSpanish ? 'Ingresos/año' : 'Revenue/year'}
                  </p>
                </div>
              </div>
            </div>

            {/* Combined Recommendation */}
            <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
              <h4 className="font-semibold text-amber-400 mb-3">
                {isSpanish ? '💡 ESTRATEGIA COMBINADA (Recomendado)' : '💡 COMBINED STRATEGY (Recommended)'}
              </h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">
                    {isSpanish ? 'Potencial primer mes:' : 'First month potential:'}
                  </p>
                  <p className="text-3xl font-bold text-amber-400">
                    €{results.comparison.combined_first_month?.toLocaleString()}
                  </p>
                </div>
                <ArrowRight className="w-8 h-8 text-amber-400" />
                <Button
                  onClick={onStartDemo}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  data-testid="calc-start-demo"
                >
                  {isSpanish ? 'Probar Demo Gratis' : 'Try Free Demo'}
                </Button>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              {isSpanish 
                ? '* Estimaciones basadas en tasas de conversión promedio del sector. Resultados reales pueden variar.'
                : '* Estimates based on industry average conversion rates. Actual results may vary.'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReactivationCalculator;
